import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';

export const usePermissions = () => {
  const { permissions: authPermissions, user } = useAuth();
  const tenant = useTenant();

  // Use tenant permissions if available, otherwise fall back to auth permissions
  const permissions = tenant?.permissions || authPermissions;

  const hasPermission = useMemo(
    () => (requiredPermissions: string | string[]): boolean => {
      if (!user) return false;

      // Platform users with admin role have all permissions. Checked before the empty-
      // permissions early return below: platform admins get access via platform_roles rather
      // than having every fine-grained permission string explicitly listed, so `permissions`
      // is routinely empty for them — that early return made this bypass unreachable and every
      // permission-gated route (PermissionGuard defaults to rendering nothing on denial) went
      // blank for platform admins, most visibly right after impersonation ends and the admin
      // session is restored.
      if (user.user_type === 'platform_user' &&
          (user.platform_roles?.includes('admin') ||
           user.platform_roles?.includes('super_admin'))) {
        return true;
      }

      if (!permissions.length) return false;

      const permissionsToCheck = Array.isArray(requiredPermissions)
        ? requiredPermissions
        : [requiredPermissions];

      // Check if user has all required permissions
      return permissionsToCheck.every(permission =>
        permissions.includes(permission)
      );
    },
    [permissions, user]
  );

  const hasAnyPermission = useMemo(
    () => (requiredPermissions: string[]): boolean => {
      if (!user) return false;

      // Platform users with admin role have all permissions — see hasPermission above for why
      // this must run before the empty-permissions check.
      if (user.user_type === 'platform_user' &&
          (user.platform_roles?.includes('admin') ||
           user.platform_roles?.includes('super_admin'))) {
        return true;
      }

      if (!permissions.length) return false;

      // Check if user has any of the required permissions
      return requiredPermissions.some(permission =>
        permissions.includes(permission)
      );
    },
    [permissions, user]
  );

  const canAccessPlatformAdmin = useMemo(
    () => user?.user_type === 'platform_user' && 
         (user.platform_roles?.includes('admin') || 
          user.platform_roles?.includes('super_admin') ||
          user.platform_roles?.includes('support')),
    [user]
  );

  const canAccessOrganization = useMemo(
    () => (organizationId: string): boolean => {
      if (!user) return false;

      // Platform users can access all organizations
      if (user.user_type === 'platform_user') return true;

      // Organization users can only access their assigned organizations
      return hasPermission(`organization:${organizationId}:access`);
    },
    [user, hasPermission]
  );

  const getUserRole = useMemo(
    () => (organizationId?: string): string[] => {
      if (!user) return [];

      if (user.user_type === 'platform_user') {
        return user.platform_roles || [];
      }

      if (organizationId && user.organization_roles) {
        // Filter roles for specific organization if needed
        // This would require organization-specific role mapping
        return user.organization_roles;
      }

      return user.organization_roles || [];
    },
    [user]
  );

  return {
    permissions,
    user,
    hasPermission,
    hasAnyPermission,
    canAccessPlatformAdmin,
    canAccessOrganization,
    getUserRole,
  };
};

export const usePermissionCheck = (requiredPermissions: string | string[]) => {
  const { hasPermission } = usePermissions();
  return hasPermission(requiredPermissions);
};